/**
 * @summary Dismissable alert banner 
 * @see https://github.com/Office-of-Digital-Services/California-State-Web-Template-react/wiki/Components#alert
 * 
 * @example 
 * <Alert
 *   variant={'danger'}
 *   boldText={'Error:'}
 *   linkText={'View details'}
 * >
 *      {errorMessage}
 * </Alert>
 * 
 * @returns {React.ReactElement} Returns the Alert component
 */

import { Alert as BootstrapAlert, type AlertProps as BootstrapAlertProps } from 'react-bootstrap';
import { joinClasses } from '@/utils/styling';
import Icon, { IconName } from '@/components/shared/Icon';

interface AlertProps {
    children: React.ReactNode;
    variant?: BootstrapAlertProps['variant'];
    dismissible?: BootstrapAlertProps['dismissible'];
    boldText?: string;
    linkText?: string;
    showIcon?: boolean;
    onClose?: BootstrapAlertProps['onClose'];
    onClick?: () => void;
    className?: string;
}

const getIcon = (variant: BootstrapAlertProps['variant'] | undefined): IconName | undefined => {
    switch (variant) {
        case 'success':
            return 'success';
        case 'warning':
            return 'warning';
        case 'danger':
            return 'error';
        case 'info':
            return 'info';
        default:
            return;
    }
}

export default function Alert({ 
    children,
    variant,
    dismissible = false,
    boldText,
    linkText,
    showIcon = true,
    onClose,
    onClick,
    className
} : AlertProps) {
    const icon = getIcon(variant);

    return (
        <BootstrapAlert 
            variant={variant} 
            dismissible={dismissible} 
            className={joinClasses("alert-banner d-flex ps-3 pe-5", className)}
            onClose={onClose}
        >
            {showIcon && 
                <Icon name={icon} className={joinClasses("fs-4 pe-3", variant === 'danger' ? 'color-red' : undefined)} />
            }
            {boldText &&
                <span className="fw-bold">{boldText}</span>
            }
            <div className="d-flex flex-column justify-content-center">
                {children}
            </div>
            {linkText &&
                <>
                    <span className="ca-gov-icon-pipe" aria-hidden="true"></span>
                    <a onClick={onClick}>{linkText}</a>
                </>
            }
        </BootstrapAlert>
    )
};

